import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { MetaResponse } from "@/common/types/metaResponse"
import { ProductData } from "@/common/types/product.type";
import { ProductCategoryData } from "@/common/types/productCategory.type";
import { AddressData, type CompanyData } from "@/features/privateCompanies/types/companies.type"

export enum BranchHierarchyForUserAccess {
  SameOnly = 'same_only',
  SameAndDescendents = 'same_and_descendents',
}

export type GeneralInformationData = {
  id: string
  address: AddressData
  cnpj: string
  email: string
  fantasy_name: string
  official_name: string
  primary_cnae: string
  state_inscription: string
  company_logo?: {
    url: string
    content_type: string
  }
}

export type AllowedOrBlockedEstablishments = {
  id: string
  establishment_branch: {
    id: string
    general_information: EstablishmentCompanyData['general_information']
  }
}

export type RestrictionData = {
  credit_limit: string
  minimum_kilometers_usage_between_consumptions: number
  maximum_kilometers_usage_between_consumptions: number
  minimum_seconds_interval_between_consumptions: number
  minimum_seconds_usage_between_consumptions: number
  maximum_seconds_usage_between_consumptions: number
  establishment_block_mode: string
  allowed_or_blocked_establishments: AllowedOrBlockedEstablishments[]
  period_restrictions: {
    id: string
    blocked: boolean
    intervals: {
      id: string
      end_time: string
      start_time: string
    }[]
    week_day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'
  }[]
  product_category_restrictions: {
    id: string
    product_category: {
      id: string
      display_name: string
      products_count: number
    }
    minimum_kilometers_usage_between_consumptions: number
    maximum_kilometers_usage_between_consumptions: number
    minimum_seconds_usage_between_consumptions: number
    maximum_seconds_usage_between_consumptions: number
  }[]
  product_restrictions: {
    id: string
    credit_limit: string
    product: {
      id: string
      category: {
        id: string
        display_name: string
        products_count: number
      }
      display_name: string
      unit_for_quantity: string
    }
    quantity_limit: string
  }[]
}

export type CompanyBranchProductGrantedCreditData = {
  id?: string;
  product_id: string;
  granted_credit: string;
  granted_quantity?: string
  product: ProductData;
}

export type CompanyBranchProductCategoryGrantedCreditData = {
  id?: string;
  product_category_id: string
  granted_credit: string
  granted_quantity?: string
  product_category: ProductCategoryData
  product_granted_credits?: CompanyBranchProductGrantedCreditData[]
}

export type FiscalDocumentInformationData = {
  fiscal_document_kind?: string
  consumption_observations?: string
  service_observations?: string
}

export type CompanyBranchData = {
  id: string,
  client_users_count: number,
  created_at: string,
  display_name: string,
  equipments_count: number,
  parent_id: string,
  parent_display_name: string,
  kind?: string,
  master_client_user?: {
    id: string
    full_name: string
    branch: {
      id: string
    }
  }
  general_information?: GeneralInformationData
  restriction: RestrictionData
  consumed_credit: string
  remaining_credit: string
  granted_credit: string
  government_allocation_requested_credit: string
  product_category_granted_credits?: CompanyBranchProductCategoryGrantedCreditData[]
  fiscal_document_information?: FiscalDocumentInformationData
  company: CompanyData;
  should_have_own_bill_when_dynamic: boolean
  name_on_card?: string
  total_government_allocation_distributed_to_branch: string
  status: string
  additional_documents: {
    id: string
    url: string
    attachment_id: string
    content_type: string
  }[]
}

export type CompanyBranchesResponse = {
  data: {
    data: CompanyBranchData[],
    meta: MetaResponse
  }
}

export type CompanyBranchResponse = {
  data: {
    data: CompanyBranchData
  }
}

export type CompanyBranchTableItem = CompanyBranchData & {
  name: string
  children: CompanyBranchTableItem[]
}

export type CompanyBranchTransformerRespose = {
  data: CompanyBranchTableItem[],
  meta: MetaResponse
}

export type Node = {
  id: string
  position: {
    x: number,
    y: number
  }
  data: CompanyBranchData & { companyId: string }
  type: string
  _createdAt: number
}

export type Edge = {
  id: string
  source: string
  target: string
}

export type CompanyBranchTransformerToStructureResponse = { nodes: Node[], edges: Edge[] }

export type CompanyBranchFinancialSummaryData = Record<string, {
  category_name: string
  remaining_total_sum: string
  granted_credit_sum: string
  granted_quantity_sum: string
  remaining_quantity_sum: string
  total_value_sum: string
  products?: {
    product_name: string
    product_unit_for_quantity: string;
    remaining_total_sum: string
    granted_credit_sum: string
    granted_quantity_sum: string
    remaining_quantity_sum: string
    total_quantity_sum: string
    total_value_sum: string
  }[]
}[]>
